import React, { useState, useRef, useEffect } from "react";
import Select from "react-select";
import Flag from "react-world-flags";
import "./PhoneNumber.css";
import countryOptions from "./CountryOptions";
import Icons from "../../Assets/Icons";
import { t } from "../../Utils/translate";

const CountryCodeSelect = ({ onChange, placeholder, cc, phoneNum, disabled }) => {
  const [selectedValue, setSelectedValue] = useState("84" || cc);
  const [menuIsOpen, setMenuIsOpen] = useState(false);
  const [filteredOptions, setFilteredOptions] = useState(countryOptions);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const modalRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        setMenuIsOpen(false);
        setSearchTerm("");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [modalRef]);

  useEffect(() => {
    if (searchTerm) {
      const lowercasedSearchTerm = searchTerm.toLowerCase();
      const newFilteredOptions = countryOptions.filter((option) => option.label.toLowerCase().includes(lowercasedSearchTerm) || option.value.includes(lowercasedSearchTerm));
      setFilteredOptions(newFilteredOptions);
      setHighlightedIndex(0);
    } else {
      setFilteredOptions(countryOptions);
      setHighlightedIndex(0);
    }
  }, [searchTerm]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (menuIsOpen) {
        switch (event.key) {
          case "ArrowDown":
            setHighlightedIndex((prevIndex) => (prevIndex < filteredOptions.length - 1 ? prevIndex + 1 : prevIndex));
            break;
          case "ArrowUp":
            setHighlightedIndex((prevIndex) => (prevIndex > 0 ? prevIndex - 1 : 0));
            break;
          case "Enter":
            if (filteredOptions[highlightedIndex]) {
              handleCountryChange(filteredOptions[highlightedIndex]);
            }
            break;
          case "Escape":
            setMenuIsOpen(false);
            setSearchTerm("");
            break;
          default:
            if (event.key.length === 1) {
              setSearchTerm((prev) => prev + event.key);
            } else if (event.key === "Backspace") {
              setSearchTerm((prev) => prev.slice(0, -1));
            }
            break;
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    const resetSearch = setTimeout(() => setSearchTerm(""), 10000);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      clearTimeout(resetSearch);
    };
  }, [menuIsOpen, searchTerm, highlightedIndex, filteredOptions]);

  const handleDropdownToggle = () => {
    if (!disabled) {
      setMenuIsOpen(!menuIsOpen);
    }
  };

  const handleCountryChange = (option) => {
    setSelectedValue(option.value);
    setMenuIsOpen(false);
    setSearchTerm("");
  };

  const [formData, setFormData] = useState({
    phoneNumber: phoneNum,
  });

  const selectedOption = countryOptions.find((option) => option.value === selectedValue);

  const getPaddingLeft = (valueLength) => {
    switch (valueLength) {
      case 2:
        return "66px";
      case 3:
        return "78px";
      case 4:
        return "89px";
      case 5:
        return "100px";
      default:
        return "65px";
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (["phoneNumber", "docId"].includes(name)) {
      if (!/^\d*$/.test(value)) return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value.trimStart().slice(0, 15),
    }));

    onChange({ countryCode: selectedValue, phoneNumber: value.trimStart() });
  };

  return (
    <div className="country-code-select-container" ref={modalRef}>
      <Select
        classNamePrefix="country-code-select"
        value={selectedOption}
        onChange={handleCountryChange}
        options={filteredOptions}
        getOptionLabel={(e) => (
          <div style={{ display: "flex", alignItems: "center" }}>
            <Flag code={e.flag} style={{ width: 20, height: 15 }} />
            <span style={{ marginLeft: 10 }}>
              {e.label} ({e.value})
            </span>
          </div>
        )}
        getOptionValue={(e) => e.value}
        menuIsOpen={menuIsOpen}
        onClick={handleDropdownToggle}
        styles={{
          option: (provided, state) => ({
            ...provided,
            backgroundColor: state.isFocused || state.data === filteredOptions[highlightedIndex] ? "#e0e0e0" : "white",
            color: "black",
          }),
        }}
        disabled={disabled}
      />

      <div className="wp-phone-sec">
        <div className={`dropdown-trigger ${disabled? "cursor-not-allowed" : ""}`} onClick={handleDropdownToggle}>
          {selectedOption ? (
            <div style={{ display: "flex", alignItems: "center" }}>
              <span className="wp-wd-cc">{"+" + selectedOption.value}</span>
            </div>
          ) : (
            <span>{placeholder}</span>
          )}
          <span className="dropdown-arrow" style={{ marginLeft: "5px" }}>
            {menuIsOpen ? <img className="arrow-open arrow-img" src={Icons.DownArrowIcon} /> : <img className="arrow-img" src={Icons.DownArrowIcon} />}
          </span>
          <div className="line-bar"></div>
        </div>

        <input
          style={{ paddingLeft: selectedOption ? getPaddingLeft(selectedOption.value.length) : "2.5rem" }}
          className="nd-inputs wp-phone-input-wd"
          type="text"
          name="phoneNumber"
          placeholder={t("phoneNumber")}
          value={formData.phoneNumber}
          onChange={handleInputChange}
          disabled={disabled}
        />
      </div>
    </div>
  );
};

export default CountryCodeSelect;
